import ReactECharts from 'echarts-for-react';

/**
 * AutonomyLadderChart — how autonomy rises and human review falls across
 * Raisy's approval modes (blog08): MANUAL → SEMI_AUTO → FULL_AUTO.
 *
 * Chart-type-first: category-to-category comparison → ECharts native grouped
 * horizontal bar. Brand colors (manual=red, semi=amber, full=green; review vs
 * autonomy series); opaque white background.
 */

const BRAND = {
  teal: '#0AB3BF',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

const MODES = ['MANUAL', 'SEMI_AUTO', 'FULL_AUTO'];
const AUTONOMY = [0, 60, 90]; // what the bot acts on its own
const REVIEW = [100, 40, 10]; // what a human still gates

/** ONE shared percentage formatter — tooltip, labels, and axis all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const AutonomyLadderChart = ({ height = 280 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${MODES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 13 },
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 92, right: 48, top: 48, bottom: 36 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.35 } },
    },
    yAxis: {
      type: 'category',
      data: MODES,
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.slate, fontSize: 13, fontWeight: 700 },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'bar',
        barWidth: '30%',
        data: AUTONOMY.map((v, i) => ({
          value: v,
          itemStyle: { color: i === 0 ? BRAND.error : i === 1 ? BRAND.warning : BRAND.success, borderRadius: [0, 6, 6, 0] },
        })),
        label: {
          show: true,
          position: 'right',
          color: BRAND.slate,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'bar',
        barWidth: '30%',
        data: REVIEW.map((v) => ({ value: v, itemStyle: { color: BRAND.teal, borderRadius: [0, 6, 6, 0], opacity: 0.85 } })),
        label: {
          show: true,
          position: 'right',
          color: BRAND.slate,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. The autonomy ladder: from MANUAL (every action gated) through SEMI_AUTO to FULL_AUTO (audit anytime).
        Autonomy rises with earned trust; human review never disappears. Hover for values.
      </p>
    </div>
  );
};
